import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Alert,
  AlertTitle,
  Autocomplete,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import {
  Repeat as RepeatIcon,
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import {
  EasyCheckButton,
  EasyCheckDialog,
  EasyCheckTextField,
  EasyCheckSelect,
  EasyCheckDatePicker,
} from '../../common/Controls';
import { chequeService } from '../../../services/cheques/chequeService';
import { formatChequeNumber } from '../../../utils/chequeValidators';
import { formatDateForDisplay, formatDateForAPI } from '../../../utils/dateUtils';
import { getAmountStep, validateAmountPrecision } from '../../../utils/currencyUtils';

const FREQUENCY_OPTIONS = [
  { label: 'Monthly', value: 1 },
  { label: 'Quarterly', value: 3 },
  { label: 'Half-Yearly', value: 6 },
  { label: 'Yearly', value: 12 },
];

const calculateRecurringDates = (startDate, monthInterval, occurrences) => {
  const dates = [];
  const baseDate = new Date(startDate);
  const originalDay = baseDate.getDate();

  for (let i = 0; i < occurrences; i++) {
    const targetMonth = baseDate.getMonth() + (i * monthInterval);
    const targetYear = baseDate.getFullYear();
    // Set day to 1 first to avoid month overflow, then set target month
    const newDate = new Date(targetYear, targetMonth, 1);
    // Get the last day of that month
    const lastDayOfMonth = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0).getDate();
    // Use the original day, clamped to the last day of the target month
    newDate.setDate(Math.min(originalDay, lastDayOfMonth));

    dates.push(newDate);
  }

  return dates;
};

const RecurringGenerationDialog = ({ open, onClose, onGenerate, bankId, currency = 'AED', beneficiaries, beneficiariesLoading }) => {
  const [suggestedNumber, setSuggestedNumber] = useState('');
  const [duplicateError, setDuplicateError] = useState('');
  const [isChecking, setIsChecking] = useState(false);

  const { control, handleSubmit, watch, reset, formState: { errors } } = useForm({
    defaultValues: {
      beneficiaryName: '',
      chqAmount: '',
      startDate: null,
      frequency: 1,
      occurrences: '',
      startingChequeNumber: '',
      remarks: '',
    }
  });

  const watchStartDate = watch('startDate');
  const watchFrequency = watch('frequency');
  const watchOccurrences = watch('occurrences');
  const watchStartingNumber = watch('startingChequeNumber');

  // Fetch suggested next cheque number when bank changes
  useEffect(() => {
    if (open && bankId) {
      chequeService.getNextChequeNumber(bankId).then(result => {
        if (result.success && result.data.nextChequeNumber) {
          setSuggestedNumber(result.data.nextChequeNumber);
        }
      });
    }
  }, [open, bankId]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      reset({
        beneficiaryName: '',
        chqAmount: '',
        startDate: null,
        frequency: 1,
        occurrences: '',
        startingChequeNumber: suggestedNumber,
        remarks: '',
      });
    }
  }, [open, reset, suggestedNumber]);

  const previewDates = useMemo(() => {
    if (!watchStartDate || !watchOccurrences || parseInt(watchOccurrences) < 1) return [];
    const d = new Date(watchStartDate);
    if (isNaN(d.getTime())) return [];
    return calculateRecurringDates(watchStartDate, parseInt(watchFrequency) || 1, parseInt(watchOccurrences) || 1);
  }, [watchStartDate, watchFrequency, watchOccurrences]);

  const onSubmit = async (data) => {
    const dates = calculateRecurringDates(data.startDate, parseInt(data.frequency), parseInt(data.occurrences));
    const startNum = data.startingChequeNumber ? data.startingChequeNumber.trim() : '';
    const isNumeric = /^\d+$/.test(startNum);

    const rows = dates.map((date, i) => {
      let chequeNumber = '';
      if (startNum && isNumeric) {
        const val = parseInt(startNum, 10) + i;
        chequeNumber = formatChequeNumber(val.toString());
      }

      return {
        beneficiaryName: data.beneficiaryName,
        chqAmount: parseFloat(data.chqAmount),
        chqDate: formatDateForAPI(date),
        chequeNumber,
        remarks: data.remarks || '',
        source: 'recurring',
      };
    });

    // Check for duplicate cheque numbers against the database
    const chequeNumbers = rows.map(r => r.chequeNumber).filter(Boolean);
    if (chequeNumbers.length > 0 && bankId) {
      setIsChecking(true);
      setDuplicateError('');
      try {
        const dupResponse = await chequeService.checkDuplicateChequeNumbers(bankId, chequeNumbers);
        if (dupResponse?.success && dupResponse.data?.length > 0) {
          const dupNums = dupResponse.data.map(d => `${d.ChequeNumber || d.chequeNumber} (${d.ExistingStatus || d.existingStatus})`);
          setDuplicateError(`Cheque numbers already exist: ${dupNums.join(', ')}`);
          setIsChecking(false);
          return;
        }
      } catch (err) {
        // If check fails, let it proceed — backend will catch duplicates
      }
      setIsChecking(false);
    }

    setDuplicateError('');
    onGenerate(rows);
    onClose();
  };

  const handleClose = () => {
    reset();
    setDuplicateError('');
    onClose();
  };

  return (
    <EasyCheckDialog
      open={open}
      onClose={handleClose}
      title="Generate Recurring Cheques"
      titleIcon={<RepeatIcon />}
      variant="form"
      maxWidth="md"
      actions={
        <>
          <EasyCheckButton buttonType="form" variant="outlined" onClick={handleClose}>
            Cancel
          </EasyCheckButton>
          <EasyCheckButton buttonType="form" onClick={handleSubmit(onSubmit)} loading={isChecking}>
            Generate {watchOccurrences ? `(${watchOccurrences})` : ''}
          </EasyCheckButton>
        </>
      }
    >
      <Box sx={{ pt: 1 }}>
        {duplicateError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDuplicateError('')}>
            {duplicateError}
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name="beneficiaryName"
              control={control}
              rules={{
                required: 'Beneficiary name is required',
                maxLength: { value: 200, message: 'Maximum 200 characters allowed' }
              }}
              render={({ field: { onChange, value, ...field } }) => (
                <Autocomplete
                  {...field}
                  freeSolo
                  options={beneficiaries.map(b => b.beneficiaryName)}
                  value={value || ''}
                  onChange={(event, newValue) => onChange(newValue || '')}
                  onInputChange={(event, newInputValue) => onChange(newInputValue || '')}
                  loading={beneficiariesLoading}
                  renderInput={(params) => (
                    <EasyCheckTextField
                      {...params}
                      variant="form"
                      label="Beneficiary *"
                      error={!!errors.beneficiaryName}
                      helperText={errors.beneficiaryName?.message}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {beneficiariesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      {option}
                    </Box>
                  )}
                  filterOptions={(options, { inputValue }) => {
                    const filtered = options.filter(option =>
                      option.toLowerCase().includes(inputValue.toLowerCase())
                    );
                    if (inputValue !== '' && !options.some(option =>
                      option.toLowerCase() === inputValue.toLowerCase()
                    )) {
                      filtered.push(inputValue);
                    }
                    return filtered;
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="chqAmount"
              control={control}
              rules={{
                required: 'Amount is required',
                min: { value: 0.01, message: 'Amount must be greater than 0' },
                max: { value: 9999999999.99, message: 'Amount too large' },
                validate: (v) => validateAmountPrecision(v, currency)
              }}
              render={({ field }) => (
                <EasyCheckTextField
                  {...field}
                  variant="form"
                  label="Amount *"
                  type="number"
                  inputProps={{ min: 0.01, step: getAmountStep(currency) }}
                  error={!!errors.chqAmount}
                  helperText={errors.chqAmount?.message}
                  fullWidth
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="startDate"
              control={control}
              rules={{
                required: 'Start date is required',
                validate: (value) => {
                  if (!value) return true;
                  const d = new Date(value);
                  if (isNaN(d.getTime())) return 'Invalid date';
                  return true;
                }
              }}
              render={({ field }) => (
                <EasyCheckDatePicker
                  {...field}
                  label="Start Date *"
                  variant="form"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.startDate,
                      helperText: errors.startDate?.message,
                    }
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="frequency"
              control={control}
              render={({ field }) => (
                <EasyCheckSelect
                  {...field}
                  variant="form"
                  label="Frequency"
                  fullWidth
                >
                  {FREQUENCY_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </EasyCheckSelect>
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="occurrences"
              control={control}
              rules={{
                required: 'Number of occurrences is required',
                min: { value: 1, message: 'Minimum 1 occurrence' },
                max: { value: 120, message: 'Maximum 120 occurrences' }
              }}
              render={({ field }) => (
                <EasyCheckTextField
                  {...field}
                  variant="form"
                  label="Occurrences *"
                  type="number"
                  inputProps={{ min: 1, max: 120 }}
                  error={!!errors.occurrences}
                  helperText={errors.occurrences?.message || 'Number of cheques to generate (1-120)'}
                  fullWidth
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="startingChequeNumber"
              control={control}
              rules={{
                validate: (value) => {
                  if (!value || value.trim() === '') return true;
                  const cleanNumber = value.trim();
                  if (!/^\d+$/.test(cleanNumber)) return 'Must contain only digits';
                  if (cleanNumber.length > 6) return 'Maximum 6 digits';
                  return true;
                }
              }}
              render={({ field }) => (
                <EasyCheckTextField
                  {...field}
                  variant="form"
                  label="Starting Cheque #"
                  error={!!errors.startingChequeNumber}
                  helperText={errors.startingChequeNumber?.message || (suggestedNumber ? `Suggested: ${suggestedNumber}` : 'Optional')}
                  fullWidth
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="remarks"
              control={control}
              render={({ field }) => (
                <EasyCheckTextField
                  {...field}
                  variant="form"
                  label="Remarks"
                  fullWidth
                />
              )}
            />
          </Grid>

          {/* Preview */}
          {previewDates.length > 0 && (
            <Grid item xs={12}>
              <Alert severity="info">
                <AlertTitle>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon fontSize="small" />
                    Preview ({previewDates.length} cheques)
                  </Box>
                </AlertTitle>
                <Typography variant="body2">
                  {previewDates.slice(0, 6).map(d => formatDateForDisplay(d)).join(', ')}
                  {previewDates.length > 6 && ` ... (+${previewDates.length - 6} more)`}
                </Typography>
                {watchStartingNumber && /^\d+$/.test(watchStartingNumber.trim()) && (
                  <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
                    Cheque numbers: {previewDates.slice(0, 6).map((_, i) => {
                      const val = parseInt(watchStartingNumber.trim(), 10) + i;
                      return val.toString().padStart(Math.max(watchStartingNumber.trim().length, 6), '0');
                    }).join(', ')}
                    {previewDates.length > 6 && ' ...'}
                  </Typography>
                )}
              </Alert>
            </Grid>
          )}
        </Grid>
      </Box>
    </EasyCheckDialog>
  );
};

export default RecurringGenerationDialog;
